import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { loadCalendarFromLocalStorage } from '@/lib/calendarParser'

// =============================================================================
// FOCUS MODE: Squirt B1 Only
// =============================================================================
// We're focusing on one level to perfect the algorithm before expanding.
// This will be removed once we're confident in the matching quality.
const FOCUS_LEVEL = 'boys_squirt_10u'
const FOCUS_LEVEL_DETAIL = 'B1'

// Performance defaults for real-time matching (only used if user hasn't set filters)
const DEFAULT_RADIUS_MILES = 45  // Pre-filter to 45 miles for speed
const DEFAULT_DATE_RANGE_DAYS = 30  // Only look 30 days ahead

// Match validation alignment: use same limits as get_posting_matches_for_validation
const MAX_POSTINGS_LIMIT = 200  // Allow more postings to check
const MAX_TEAMS_LIMIT = 200     // Allow more teams to check

// Haversine distance calculation (miles)
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 3959 // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

// Calculate date 30 days from now
function getDefaultEndDate() {
  const date = new Date()
  date.setDate(date.getDate() + DEFAULT_DATE_RANGE_DAYS)
  return date.toISOString().split('T')[0]
}

export function useScrimmageSearch(sport = 'hockey') {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [teamProfile, setTeamProfile] = useState(null)
  const [isPersonalized, setIsPersonalized] = useState(false)
  const [profileLoaded, setProfileLoaded] = useState(false)
  // Calendar sync status for the logged-in team
  const [calendarSyncStatus, setCalendarSyncStatus] = useState({
    hasEvents: null,      // true/false/null (unknown)
    eventCount: 0,
    lastSyncSucceeded: null,
    syncStatus: null,
    warning: null         // Warning message to show user
  })
  
  // Filter state
  const [filters, setFilters] = useState({
    searchLat: null,
    searchLng: null,
    radiusMiles: null,
    level: null,
    levelDetail: null,
    dateStart: null,
    dateEnd: null,
    postingType: null,
    refereeType: null,
    homeAndHome: null,
    calendarEnabled: false,
    conflictMode: 'any',
    includeOwnTeam: false,
    currentTeamId: null,
    // Precomputed matches mode
    usePrecomputedMatches: false,
    matchPerspective: 'demand' // 'demand' or 'supply'
  })

  // Load team profile for personalized defaults
  const loadTeamProfile = useCallback(async (teamId) => {
    if (!teamId) {
      setTeamProfile(null)
      setProfileLoaded(true)
      return
    }

    try {
      const { data, error: profileError } = await supabase
        .rpc('get_team_profile_with_defaults', { team_id_param: teamId })

      if (profileError) {
        console.warn('Error loading team profile:', profileError)
        setTeamProfile(null)
      } else if (data) {
        setTeamProfile(data)
      }
    } catch (err) {
      console.warn('Error loading team profile:', err)
      setTeamProfile(null)
    } finally {
      setProfileLoaded(true)
    }
  }, [])

  // Apply team profile defaults to filters
  const applyProfileDefaults = useCallback((profile) => {
    if (!profile) return

    const newFilters = {}

    // Apply level filter from team
    if (profile.team_level) {
      newFilters.level = profile.team_level
    }
    if (profile.team_level_detail) {
      newFilters.levelDetail = profile.team_level_detail
    }

    // Apply radius from profile
    if (profile.preferred_radius_miles) {
      newFilters.radiusMiles = profile.preferred_radius_miles
    }

    // Apply location from team
    if (profile.team_latitude && profile.team_longitude) {
      newFilters.searchLat = profile.team_latitude
      newFilters.searchLng = profile.team_longitude
    }

    // Apply home and home preference
    if (profile.prefer_home_and_home) {
      newFilters.homeAndHome = true
    }

    // Apply referee preference (only if specific, not 'any')
    if (profile.preferred_referee_type && profile.preferred_referee_type !== 'any') {
      newFilters.refereeType = profile.preferred_referee_type
    }

    // Enable calendar matching if team has events
    newFilters.calendarEnabled = true
    newFilters.conflictMode = 'flexible'

    setFilters(prev => ({
      ...prev,
      ...newFilters
    }))
  }, [])

  // Toggle between personalized and all scrimmages view
  const togglePersonalized = useCallback(() => {
    if (isPersonalized) {
      // Switch to showing all - clear profile-based filters
      setFilters(prev => ({
        ...prev,
        level: null,
        levelDetail: null,
        radiusMiles: null,
        searchLat: null,
        searchLng: null,
        homeAndHome: null,
        refereeType: null,
        calendarEnabled: false,
        conflictMode: 'any'
      }))
      setIsPersonalized(false)
    } else {
      // Switch to personalized - reapply profile defaults
      if (teamProfile) {
        applyProfileDefaults(teamProfile)
      }
      setIsPersonalized(true)
    }
  }, [isPersonalized, teamProfile, applyProfileDefaults])

  // Transform precomputed match results to card-compatible format
  const transformPrecomputedMatches = useCallback((matches) => {
    if (!matches || !Array.isArray(matches)) return []
    
    return matches.map(match => ({
      posting: {
        id: match.posting_id,
        date: match.opportunity_date,
        start_time: match.opportunity_start_time,
        end_time: match.opportunity_end_time,
        team_level: match.team_level,
        level_detail: match.level_detail,
        rink_name: match.rink_name,
        city: match.rink_city,
        state: match.rink_state,
        rink_address: match.rink_address,
        posting_type: match.posting_type || 'available',
        team_id: match.posting_team_id,
        referee_type: match.referee_type,
        scrimmage_fee: match.scrimmage_fee,
        home_and_home: match.home_and_home,
        prefer_non_district: match.prefer_non_district,
        notes: match.posting_notes,
        auto_generated: match.auto_generated,
        confidence_score: match.confidence_score || match.solo_confidence_score
      },
      team_info: {
        name: match.other_team_name,
        association_name: match.other_team_association_name,
        city: match.other_team_city,
        state: match.other_team_state,
        team_level: match.team_level,
        level_detail: match.level_detail,
        data_source: match.other_team_data_source,
        claimed_at: match.other_team_claimed_at
      },
      // Manager info is NULL for supply view (demand teams don't have contact info)
      manager_info: match.manager_name || match.manager_email || match.manager_phone
        ? {
            name: match.manager_name,
            email: match.manager_email,
            phone: match.manager_phone
          }
        : null,
      // Perspective field for UI handling (e.g., disable Contact for supply view)
      match_perspective: match.match_perspective,
      distance_miles: match.distance_miles,
      has_conflict: false,
      conflict_type: null,
      conflicting_events: null,
      match_tier: match.schedule_tier,
      match_score: match.combined_score,
      match_reason: match.match_reason,
      // Combined score fields
      combined_score: match.combined_score,
      solo_confidence_score: match.solo_confidence_score,
      schedule_fit_score: match.schedule_fit_score,
      distance_score: match.distance_score,
      penalty_applied: match.penalty_applied,
      same_association: match.same_association,
      same_district: match.same_district,
      score_badge: match.score_badge
    }))
  }, [])

  // Helper: Fetch calendar events for a team on a specific date (Central Time)
  const fetchCalendarEventsForDate = useCallback(async (teamId, date) => {
    if (!teamId || !date) return []
    
    // Minnesota is Central Time - query wider UTC range for safety
    const startUTC = `${date}T06:00:00Z`
    const nextDay = new Date(date)
    nextDay.setDate(nextDay.getDate() + 1)
    const nextDayStr = nextDay.toISOString().split('T')[0]
    const endUTC = `${nextDayStr}T06:00:00Z`
    
    const { data, error } = await supabase
      .from('calendar_events')
      .select('id, title, start_time, end_time, event_type, location')
      .eq('team_id', teamId)
      .gte('start_time', startUTC)
      .lt('start_time', endUTC)
      .order('start_time', { ascending: true })

    if (error) {
      console.warn('Error fetching calendar events:', error)
      return []
    }
    return data || []
  }, [])

  const search = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Use REAL-TIME scoring if enabled and team is set (like match-validation)
      if (filters.usePrecomputedMatches && filters.currentTeamId) {
        // Get team details first
        const { data: teamData } = await supabase
          .from('teams')
          .select('id, name, team_level, level_detail, latitude, longitude')
          .eq('id', filters.currentTeamId)
          .single()

        if (!teamData) throw new Error('Team not found')

        // =============================================================================
        // FOCUS MODE CHECK: Only process Squirt B1 teams
        // =============================================================================
        if (teamData.team_level !== FOCUS_LEVEL || teamData.level_detail !== FOCUS_LEVEL_DETAIL) {
          console.log(`[useScrimmageSearch] Team ${teamData.name} is ${teamData.team_level}/${teamData.level_detail}, not Squirt B1 - showing empty results`)
          setResults([])
          setLoading(false)
          return
        }

        // =============================================================================
        // CHECK LOGGED-IN TEAM'S CALENDAR SYNC STATUS
        // =============================================================================
        // This is critical: if the team has no calendar events, the schedule_fit 
        // scoring will treat every day as "free" which is misleading
        const { data: calendarCheck } = await supabase
          .from('calendar_events')
          .select('id')
          .eq('team_id', teamData.id)
          .gte('start_time', new Date().toISOString())
          .limit(1)

        const { data: teamCalendarInfo } = await supabase
          .from('teams')
          .select('calendar_sync_status, last_calendar_sync_succeeded')
          .eq('id', teamData.id)
          .single()

        const hasCalendarEvents = calendarCheck && calendarCheck.length > 0
        
        if (!hasCalendarEvents) {
          console.warn(`[useScrimmageSearch] ⚠️ Team ${teamData.name} has NO calendar events synced!`)
          console.warn(`[useScrimmageSearch] Calendar sync status: ${teamCalendarInfo?.calendar_sync_status}`)
          console.warn(`[useScrimmageSearch] Schedule-based scoring will assume all days are free.`)
          
          setCalendarSyncStatus({
            hasEvents: false,
            eventCount: 0,
            lastSyncSucceeded: teamCalendarInfo?.last_calendar_sync_succeeded,
            syncStatus: teamCalendarInfo?.calendar_sync_status,
            warning: `Your team's calendar has no events synced. Schedule-based matching may be inaccurate. Try syncing your calendar from Team Settings or Match Validation.`
          })
        } else {
          // Count total future events
          const { count } = await supabase
            .from('calendar_events')
            .select('id', { count: 'exact', head: true })
            .eq('team_id', teamData.id)
            .gte('start_time', new Date().toISOString())

          setCalendarSyncStatus({
            hasEvents: true,
            eventCount: count || 0,
            lastSyncSucceeded: teamCalendarInfo?.last_calendar_sync_succeeded,
            syncStatus: teamCalendarInfo?.calendar_sync_status,
            warning: null
          })
        }

        const perspective = filters.matchPerspective || 'demand'
        const scoredResults = []

        // =============================================================================
        // WIRE SIDEBAR FILTERS INTO REAL-TIME BRANCH
        // =============================================================================
        // Use user-specified filters if set, otherwise use sensible defaults
        const today = new Date().toISOString().split('T')[0]
        
        // Date filters: respect user settings, default to 30 days if unset
        const effectiveDateStart = filters.dateStart || today
        const effectiveDateEnd = filters.dateEnd || getDefaultEndDate()
        
        // Radius filter: respect user settings, default to 45 miles if unset
        // radiusMiles can be 0 (from "Any distance"), treat as unlimited
        const effectiveRadius = filters.radiusMiles === null || filters.radiusMiles === undefined
          ? DEFAULT_RADIUS_MILES
          : (filters.radiusMiles === 0 ? 9999 : filters.radiusMiles)

        console.log(`[useScrimmageSearch] Real-time search: radius=${effectiveRadius}mi, dateStart=${effectiveDateStart}, dateEnd=${effectiveDateEnd}`)

        if (perspective === 'demand') {
          // ========================================
          // DEMAND (AWAY): Find other teams' solo ice where YOU could go
          // ========================================
          
          // Build query with focus level filter
          let query = supabase
            .from('scrimmage_postings')
            .select(`
              id,
              date,
              start_time,
              end_time,
              team_level,
              level_detail,
              rink_name,
              city,
              state,
              rink_address,
              latitude,
              longitude,
              status,
              confidence_score,
              posting_type,
              referee_type,
              scrimmage_fee,
              home_and_home,
              prefer_non_district,
              notes,
              auto_generated,
              team_id,
              teams!inner(
                id,
                name,
                display_name,
                association_name,
                city,
                state,
                latitude,
                longitude,
                data_source,
                claimed_at
              )
            `)
            .eq('status', 'active')
            .eq('team_level', FOCUS_LEVEL)  // Force Squirt B1 only
            .eq('level_detail', FOCUS_LEVEL_DETAIL)
            .neq('team_id', teamData.id) // Exclude own team
            .gte('date', effectiveDateStart)  // Use user's start date or today
            .lte('date', effectiveDateEnd)    // Use user's end date or +30 days
            .order('date', { ascending: true })
            .limit(MAX_POSTINGS_LIMIT)  // Aligned with match-validation limits

          const { data: postings, error: postingsError } = await query

          if (postingsError) throw postingsError

          console.log(`[useScrimmageSearch] Found ${postings?.length || 0} Squirt B1 postings within date range`)

          for (const posting of postings || []) {
            // Apply radius filter early - skip if outside effective radius
            if (teamData.latitude && teamData.longitude && posting.latitude && posting.longitude) {
              const distance = haversineDistance(
                teamData.latitude, teamData.longitude,
                posting.latitude, posting.longitude
              )
              if (distance > effectiveRadius) continue
            }

            // Apply posting type filter if set (available vs wanted)
            if (filters.postingType && posting.posting_type !== filters.postingType) continue
            
            // Apply referee type filter if set
            if (filters.refereeType && posting.referee_type !== filters.refereeType) continue
            
            // Apply home-and-home filter if set
            if (filters.homeAndHome && !posting.home_and_home) continue

            // Get real-time score from the algorithm
            const { data: scoreData, error: scoreError } = await supabase
              .rpc('get_combined_match_score', {
                p_posting_id: posting.id,
                p_demand_team_id: teamData.id
              })

            if (scoreError || !scoreData || scoreData.length === 0) continue
            const score = scoreData[0]
            
            // Skip if no valid score (hard blocked)
            if (score.combined_score === null) continue
            
            // REMOVED threshold - show all valid matches like match-validation
            // The UI can filter/sort by score

            // Fetch YOUR calendar events for this date
            const yourCalendarEvents = await fetchCalendarEventsForDate(teamData.id, posting.date)

            scoredResults.push({
              posting: {
                id: posting.id,
                date: posting.date,
                start_time: posting.start_time,
                end_time: posting.end_time,
                team_level: posting.team_level,
                level_detail: posting.level_detail,
                rink_name: posting.rink_name,
                city: posting.city,
                state: posting.state,
                rink_address: posting.rink_address,
                posting_type: posting.posting_type || 'available',
                team_id: posting.team_id,
                referee_type: posting.referee_type,
                scrimmage_fee: posting.scrimmage_fee,
                home_and_home: posting.home_and_home,
                prefer_non_district: posting.prefer_non_district,
                notes: posting.notes,
                auto_generated: posting.auto_generated,
                confidence_score: posting.confidence_score
              },
              team_info: {
                name: posting.teams.display_name || posting.teams.name,
                association_name: posting.teams.association_name,
                city: posting.teams.city,
                state: posting.teams.state,
                data_source: posting.teams.data_source,
                claimed_at: posting.teams.claimed_at
              },
              manager_info: null,
              match_perspective: 'demand',
              distance_miles: score.distance_miles,
              has_conflict: false,
              conflict_type: null,
              conflicting_events: null,
              match_tier: score.schedule_fit_score >= 85 ? 1 : score.schedule_fit_score >= 70 ? 2 : 3,
              match_score: score.combined_score,
              match_reason: score.match_reason,
              combined_score: score.combined_score,
              solo_confidence_score: score.solo_confidence_score,
              schedule_fit_score: score.schedule_fit_score,
              distance_score: score.distance_score,
              penalty_applied: score.penalty_applied,
              same_association: score.same_association,
              same_district: score.same_district,
              posting_district: score.posting_district,
              demand_district: score.demand_district,
              score_badge: score.combined_score >= 80 ? 'excellent' : score.combined_score >= 60 ? 'good' : 'fair',
              your_calendar_events: yourCalendarEvents
            })
          }
        } else {
          // ========================================
          // SUPPLY (HOME): Find YOUR solo ice postings and who might attend
          // ========================================
          
          // First, get YOUR team's active postings (only Squirt B1)
          const { data: myPostings, error: myPostingsError } = await supabase
            .from('scrimmage_postings')
            .select(`
              id,
              date,
              start_time,
              end_time,
              team_level,
              level_detail,
              rink_name,
              city,
              state,
              rink_address,
              latitude,
              longitude,
              status,
              confidence_score,
              posting_type,
              referee_type,
              scrimmage_fee,
              home_and_home,
              prefer_non_district,
              notes,
              auto_generated
            `)
            .eq('team_id', teamData.id)
            .eq('status', 'active')
            .eq('team_level', FOCUS_LEVEL)  // Force Squirt B1 only
            .eq('level_detail', FOCUS_LEVEL_DETAIL)
            .gte('date', effectiveDateStart)  // Use user's start date or today
            .lte('date', effectiveDateEnd)    // Use user's end date or +30 days
            .order('date', { ascending: true })
            .limit(50)  // Reasonable limit for your own postings

          if (myPostingsError) throw myPostingsError

          console.log(`[useScrimmageSearch] Found ${myPostings?.length || 0} of YOUR Squirt B1 postings`)

          // Get all other Squirt B1 teams that might want to come (within radius)
          const { data: otherTeams, error: teamsError } = await supabase
            .from('teams')
            .select('id, name, display_name, association_name, city, state, latitude, longitude, data_source, claimed_at')
            .eq('team_level', FOCUS_LEVEL)
            .eq('level_detail', FOCUS_LEVEL_DETAIL)
            .neq('id', teamData.id)
            .limit(MAX_TEAMS_LIMIT)  // Aligned with match-validation limits

          if (teamsError) throw teamsError

          console.log(`[useScrimmageSearch] Found ${otherTeams?.length || 0} other Squirt B1 teams`)

          // For each of YOUR postings, score potential guests
          for (const myPosting of myPostings || []) {
            for (const otherTeam of otherTeams || []) {
              // Apply radius filter - skip if outside effective radius
              if (teamData.latitude && teamData.longitude && otherTeam.latitude && otherTeam.longitude) {
                const distance = haversineDistance(
                  teamData.latitude, teamData.longitude,
                  otherTeam.latitude, otherTeam.longitude
                )
                if (distance > effectiveRadius) continue
              }

              // Get real-time score - YOUR posting, OTHER team as demand
              const { data: scoreData, error: scoreError } = await supabase
                .rpc('get_combined_match_score', {
                  p_posting_id: myPosting.id,
                  p_demand_team_id: otherTeam.id
                })

              if (scoreError || !scoreData || scoreData.length === 0) continue
              const score = scoreData[0]
              
              // Skip if no valid score (hard blocked)
              if (score.combined_score === null) continue
              
              // REMOVED threshold - show all valid matches like match-validation

              // Fetch OTHER team's calendar events for this date
              const theirCalendarEvents = await fetchCalendarEventsForDate(otherTeam.id, myPosting.date)

              scoredResults.push({
                posting: {
                  id: myPosting.id,
                  date: myPosting.date,
                  start_time: myPosting.start_time,
                  end_time: myPosting.end_time,
                  team_level: myPosting.team_level,
                  level_detail: myPosting.level_detail,
                  rink_name: myPosting.rink_name,
                  city: myPosting.city,
                  state: myPosting.state,
                  rink_address: myPosting.rink_address,
                  posting_type: myPosting.posting_type || 'available',
                  team_id: teamData.id,
                  referee_type: myPosting.referee_type,
                  scrimmage_fee: myPosting.scrimmage_fee,
                  home_and_home: myPosting.home_and_home,
                  prefer_non_district: myPosting.prefer_non_district,
                  notes: myPosting.notes,
                  auto_generated: myPosting.auto_generated,
                  confidence_score: myPosting.confidence_score
                },
                team_info: {
                  name: otherTeam.display_name || otherTeam.name,
                  association_name: otherTeam.association_name,
                  city: otherTeam.city,
                  state: otherTeam.state,
                  data_source: otherTeam.data_source,
                  claimed_at: otherTeam.claimed_at
                },
                manager_info: null,
                match_perspective: 'supply',
                distance_miles: score.distance_miles,
                has_conflict: false,
                conflict_type: null,
                conflicting_events: null,
                match_tier: score.schedule_fit_score >= 85 ? 1 : score.schedule_fit_score >= 70 ? 2 : 3,
                match_score: score.combined_score,
                match_reason: score.match_reason,
                combined_score: score.combined_score,
                solo_confidence_score: score.solo_confidence_score,
                schedule_fit_score: score.schedule_fit_score,
                distance_score: score.distance_score,
                penalty_applied: score.penalty_applied,
                same_association: score.same_association,
                same_district: score.same_district,
                posting_district: score.posting_district,
                demand_district: score.demand_district,
                score_badge: score.combined_score >= 80 ? 'excellent' : score.combined_score >= 60 ? 'good' : 'fair',
                // For supply view, show the OTHER team's calendar (not yours)
                their_calendar_events: theirCalendarEvents,
                other_team_name: otherTeam.display_name || otherTeam.name
              })
            }
          }
        }

        // Sort by score DESC, then by date ASC (closer dates first)
        scoredResults.sort((a, b) => {
          const scoreDiff = b.combined_score - a.combined_score
          if (scoreDiff !== 0) return scoreDiff
          return new Date(a.posting.date) - new Date(b.posting.date)
        })

        setResults(scoredResults)
        return
      }

      // Standard search path
      // Determine if we should use calendar-aware search
      const useCalendarSearch = filters.calendarEnabled
      
      let calendarEventsJson = null
      let calendarTeamId = filters.currentTeamId || null
      // Treat public browsing as "free" so visitors can see high-confidence auto-detected postings.
      let userAccessLevel = 'free'
      
      // Determine user access level
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        const { data: team } = await supabase
          .from('teams')
          .select('id, season_tier')
          .eq('user_id', session.user.id)
          .single()
        
        if (team) {
          userAccessLevel = team.season_tier === 'paid' ? 'pro' : 'free'
          if (!calendarTeamId) {
            calendarTeamId = team.id
          }
        }
      }
      
      if (useCalendarSearch) {
        // Try to get calendar from localStorage first (public users)
        const localCal = loadCalendarFromLocalStorage()
        if (localCal && localCal.events) {
          calendarEventsJson = localCal.events
        }
      }
      
      // Call appropriate RPC function
      const rpcFunction = useCalendarSearch ? 'search_scrimmages_with_calendar' : 'search_scrimmages'
      
      const excludeTeamId = !filters.includeOwnTeam && filters.currentTeamId ? filters.currentTeamId : null

      const rpcParams = useCalendarSearch ? {
        search_lat: filters.searchLat,
        search_lng: filters.searchLng,
        radius_miles: filters.radiusMiles,
        filter_sport_type: sport,
        filter_level: filters.level,
        filter_level_detail: filters.levelDetail,
        filter_date_start: filters.dateStart,
        filter_date_end: filters.dateEnd,
        filter_posting_type: filters.postingType,
        filter_referee_type: filters.refereeType,
        filter_home_and_home: filters.homeAndHome,
        exclude_team_id: excludeTeamId,
        calendar_team_id: calendarTeamId,
        calendar_events_json: calendarEventsJson,
        conflict_mode: filters.conflictMode || 'any'
        // Note: search_scrimmages_with_calendar does not have user_access_level parameter
      } : {
        search_lat: filters.searchLat,
        search_lng: filters.searchLng,
        radius_miles: filters.radiusMiles,
        filter_sport_type: sport,
        filter_level: filters.level,
        filter_level_detail: filters.levelDetail,
        filter_date_start: filters.dateStart,
        filter_date_end: filters.dateEnd,
        filter_posting_type: filters.postingType,
        filter_referee_type: filters.refereeType,
        filter_home_and_home: filters.homeAndHome,
        exclude_team_id: excludeTeamId,
        user_access_level: userAccessLevel
      }
      
      const { data, error: searchError } = await supabase
        .rpc(rpcFunction, rpcParams)

      if (searchError) throw searchError

      setResults(data || [])
    } catch (err) {
      console.error('Search error:', err)
      setError(err.message)
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [filters, sport, transformPrecomputedMatches])

  // Auto-search when filters change (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      search()
    }, 300)

    return () => clearTimeout(timer)
  }, [search])

  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const updateFilters = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }))
  }

  const clearFilters = () => {
    setFilters(prev => ({
      searchLat: null,
      searchLng: null,
      radiusMiles: null,
      level: null,
      levelDetail: null,
      dateStart: null,
      dateEnd: null,
      postingType: null,
      refereeType: null,
      homeAndHome: null,
      calendarEnabled: false,
      conflictMode: 'any',
      includeOwnTeam: prev.includeOwnTeam,
      currentTeamId: prev.currentTeamId,
      usePrecomputedMatches: false,
      matchPerspective: 'demand'
    }))
  }

  return {
    results,
    loading,
    error,
    filters,
    updateFilter,
    updateFilters,
    clearFilters,
    search,
    // Personalization features
    teamProfile,
    isPersonalized,
    profileLoaded,
    loadTeamProfile,
    applyProfileDefaults,
    togglePersonalized,
    // Calendar sync status
    calendarSyncStatus
  }
}

